import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import ReadingProgress from "./ReadingProgress";

const links = [
  { to: "/", label: "Home" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/extension-rules", label: "Extension Rules" },
  { to: "/quote", label: "Instant Quote" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Insights & Guides" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/portal", label: "Client Portal" },
];

// Links that fit alongside the logo on laptops (1280–1535px). The rest live in the menu there.
const PRIMARY = new Set(["/how-it-works", "/quote", "/gallery", "/contact", "/portal"]);

function DesktopNav({ items, className, testPrefix }) {
  return (
    <nav className={className}>
      {items.map(l => (
        <NavLink key={l.to} to={l.to} data-testid={`${testPrefix}${l.label.toLowerCase().replace(/\s/g,'-')}`}
          className={({isActive}) => `text-[13px] min-[1700px]:text-sm font-medium whitespace-nowrap hover:text-[#C79A3B] ${isActive ? "text-[#C79A3B]" : "text-[#12263A]"}`}>
          {l.label}
        </NavLink>
      ))}
      <Link to="/quote" data-testid={`${testPrefix}cta-quote`} className="de-btn-primary !text-[11px] !px-4 !py-2 tracking-normal whitespace-nowrap">Get quote</Link>
    </nav>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <header className={`de-header z-50 ${loc.pathname.startsWith("/quote") ? "" : "sticky top-0"}`}>
      <div className="px-4 sm:px-6 md:px-10 2xl:px-16 py-3 sm:py-5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 sm:gap-3 group shrink-0">
          <img src="/brand/icon-only.png" alt="" aria-hidden="true"
               className="h-8 min-[360px]:h-9 sm:h-11 w-auto shrink-0 transition-transform duration-300 group-hover:scale-105" />
          <span className="flex flex-col leading-none">
            <span className="font-black tracking-[0.02em] text-[#12263A] text-[12px] min-[360px]:text-[15px] sm:text-[17px] md:text-[19px] uppercase whitespace-nowrap">
              The <span className="text-[#C79A3B]">Extension</span> Works
            </span>
            <span className="hidden sm:block text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-[#6B7280] mt-1.5 whitespace-nowrap">
              Making home extensions simple
            </span>
          </span>
        </Link>
        {/* Logo (~380px) + 10 links + CTA only fit on one line from ~1500px. Laptops get the 5 primary links + burger for the rest. */}
        <div className="flex items-center gap-4 shrink-0">
          <DesktopNav items={links.filter(l => PRIMARY.has(l.to))} testPrefix="navc-"
            className="hidden xl:flex 2xl:hidden items-center gap-4" />
          <DesktopNav items={links} testPrefix="nav-"
            className="hidden 2xl:flex items-center gap-4 min-[1700px]:gap-7" />
          <button data-testid="mobile-menu-toggle" className="2xl:hidden p-2.5 -mr-2 shrink-0" onClick={() => setOpen(!open)} aria-label="menu" aria-expanded={open}>
            {open ? <X size={24}/> : <Menu size={24}/>}
          </button>
        </div>
      </div>
      {open && (
        <div className="2xl:hidden border-t border-[#E5E7EB] bg-[#FFFFFF] px-4 sm:px-6 py-3 flex flex-col max-h-[calc(100vh-64px)] overflow-y-auto" data-testid="mobile-menu">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={()=>setOpen(false)}
              data-testid={`m-nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
              className={`text-base font-medium py-3 border-b border-[#F0EAE0] last:border-0 ${loc.pathname===l.to?"text-[#C79A3B]":"text-[#12263A]"}`}>{l.label}</Link>
          ))}
          <Link to="/quote" onClick={()=>setOpen(false)} className="de-btn-primary justify-center mt-4 mb-2">Get quote</Link>
        </div>
      )}
      <ReadingProgress hide={loc.pathname.startsWith("/quote") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/portal")} />
    </header>
  );
}
